/**
 * DCTA-162 — Read aloud: WHEN the bank speaks and HOW its sentences play (docs/design/ux-copy/spoken.md §1.1, F30, F31).
 *
 * The policy (§1.1). "When I speak" — every customer's default, Mdm Lim included (Claude's ruling): the bank's answer to
 * a turn the customer SPOKE is read aloud — its messages, the card it draws, that card's end; a typed turn and everything
 * after it stays silent. A tap (a chip, Sign) is not a turn and changes nothing. "Always" reads every message and card;
 * "Never" nothing. Speaking is the customer's own signal: someone who says a payment aloud is somewhere they can hear
 * it back, and someone who types is not.
 *
 * The playback (F31). The objects of one answer in the screen's order, each object's sentences in order, 300 ms between
 * sentences (§4: each figure lands before the next starts). Stop, the microphone, the field, a new turn and leaving the
 * chat end it at once (`stop`); a card's end replaces what is still being read of that card (`now`). The part being
 * spoken is announced to the composer (its Stop pill, `data-speaking`) only while a sentence is actually playing.
 *
 * What it never does: send a word (it names objects — `adapters/speech.ts`), choose what is spoken (the bank writes it,
 * the never-spoken list included, §1.3), answer, sign or advance anything. A cap or a failure is silence.
 */
import type { AudioOut } from "../adapters/audio-out.js";
import type { SpeechPart, SpeechRef, SpeechSource } from "../adapters/speech.js";
import type { BundleView, Channel, DraftOutcome, ReadAloudChoice, RefusedStep } from "../api/types.js";
import { collapseRefusals, outcomePlan } from "./outcome-plan.js";
import { replyOf } from "./surfaces.js";

/** spoken.md §1.1's three modes (the bank's own names, B43). */
export type ReadAloudMode = ReadAloudChoice;

/** spoken.md §4 and F31: the gap between two sentences (ms). */
export const GAP_MS = 300;

/** §1.1: does the bank speak, given the mode and how the customer's last turn came (none yet: silent unless Always)? */
export function speaks(mode: ReadAloudMode, last: Channel | undefined): boolean {
  if (mode === "always") return true;
  if (mode === "never") return false;
  return last === "voice";
}

export interface ReadAloudDeps {
  readonly source: SpeechSource;
  /** No speaker in this browser: nothing is ever asked of the bank. */
  readonly out: AudioOut | undefined;
  readonly mode: () => ReadAloudMode;
  /** The part playing now, or `undefined` when the bank is quiet again. */
  readonly onSpeaking?: (part: SpeechPart | undefined) => void;
}

export class ReadAloud {
  readonly #d: ReadAloudDeps;
  /** How the customer's last turn came; taps are not turns. */
  #last: Channel | undefined;
  #queue: SpeechRef[] = [];
  /** The run in progress; aborted by `stop` — every await in the run reads it before it touches anything. */
  #run: AbortController | undefined;
  #part: SpeechPart | undefined;

  constructor(deps: ReadAloudDeps) {
    this.#d = deps;
  }

  /** A sentence is playing (or between two of one run). */
  get speaking(): boolean {
    return this.#part !== undefined;
  }

  /** A turn the customer said (`voice`) or typed: what was being read stops — they are talking again. */
  turn(channel: Channel): void {
    this.stop();
    this.#last = channel;
  }

  /** A bank object the screen just drew: read aloud if the policy says so, after what is queued — or instead of it (`now`). */
  speak(ref: SpeechRef, o: { readonly now?: boolean } = {}): void {
    if (this.#d.out === undefined || !speaks(this.#d.mode(), this.#last)) return;
    if (o.now === true) this.stop();
    this.#queue.push(ref);
    if (this.#run === undefined) void this.#play(this.#d.out);
  }

  /** Silence at once: the sentence stops, the queue goes, nothing more is fetched. */
  stop(): void {
    this.#queue = [];
    this.#run?.abort();
    this.#run = undefined;
    this.#show(undefined);
  }

  /**
   * Inside the customer's tap (iOS plays nothing a page starts by itself). spoken.md F31 unlocks on the microphone's tap
   * (`mic`: When I speak and Always); any other tap (`tap`: Send, a chip) only in Always — the one mode where a typed turn
   * is read aloud. In Never no audio context is ever made, and a customer who only types makes none either.
   */
  unlock(tap: "mic" | "tap"): void {
    const mode = this.#d.mode();
    if (mode === "always" || (mode === "when_i_speak" && tap === "mic")) this.#d.out?.unlock();
  }

  async #play(out: AudioOut): Promise<void> {
    const run = new AbortController();
    this.#run = run;
    const { signal } = run;
    const source = this.#d.source;
    let first = true;
    try {
      while (!signal.aborted) {
        const ref = this.#queue.shift();
        if (ref === undefined) break;
        const plan = await source.plan(ref, signal);
        if (plan === undefined || signal.aborted) continue;
        let next = source.audio(plan, 0, signal);
        for (let n = 0; n < plan.count; n += 1) {
          const audio = await next;
          if (audio === undefined || signal.aborted) break;
          // The next sentence is fetched while this one plays.
          next = n + 1 < plan.count ? source.audio(plan, n + 1, signal) : Promise.resolve(undefined);
          if (!first && !(await gap(signal))) break;
          first = false;
          // The pill shows when the sound starts — never for a sentence the browser will not play.
          if (!(await out.play(audio, signal, () => this.#show(ref.part)))) {
            // The browser will not play (locked, undecodable): silence for the rest of this run.
            run.abort();
            break;
          }
        }
      }
    } finally {
      if (this.#run === run) {
        this.#run = undefined;
        this.#queue = [];
        this.#show(undefined);
      }
    }
  }

  #show(part: SpeechPart | undefined): void {
    if (this.#part === part) return;
    this.#part = part;
    this.#d.onSpeaking?.(part);
  }
}

/** 300 ms, or less when the run is stopped meanwhile: true when the gap ran its course. */
function gap(signal: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve(true);
    }, GAP_MS);
    const onAbort = (): void => {
      clearTimeout(timer);
      resolve(false);
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * What an answer drew that the bank may speak, in the screen's order (`Chat.#run`): a card's refused steps (drawn
 * above it) then its read-back; small talk's reply; the refusals, then the question on the screen (the first one).
 * DCTA-176 F44/F50: the same plan the chat draws (`outcome-plan.ts`) — identical refusals once, and nothing asked under
 * the identity check's turn refusal (spoken.md §3.8: 24a and 24c said once). A protective action's result lines and an
 * empty "Nothing was sent." have no spoken form (§3). DCTA-199 F51 (docs/design/v3/spoken-cta.md RF27, decision 6): ONE
 * refusal per answer — the first in the screen's order, with or without a card; the rest are on the screen.
 */
export function outcomeParts(outcome: DraftOutcome, bundle: BundleView | undefined): SpeechRef[] {
  const draft = outcome.draft_id;
  // DCTA-209 (§13.9, EN16): a protective action the bank ran and names a spoken kind for (the chat's stop: "stopped") is
  // read first, as it is drawn first — by the bank's kind, never the app's words — and only where it is drawn: an answer
  // with a question draws the question, not the result lines (`Chat.#otherOutcome`). A read speaks itself once drawn.
  const ran: SpeechRef[] = (outcome.executed ?? []).flatMap((e) => (e.read === undefined && typeof e.speech_read === "string" && e.speech_read !== "" ? [{ part: "reply" as const, read: e.speech_read }] : []));
  const refusals = (steps: readonly RefusedStep[]): SpeechRef[] => steps.slice(0, 1).map((r) => ({ part: "refusal", draft_id: draft, step_id: r.step_id }));
  if (bundle !== undefined) return [...refusals(collapseRefusals(bundle.refused_steps)), { part: "readback", draft_id: draft, bundle_id: bundle.bundle_id }];
  // Only what the screen draws (the audio must match it): a reply with no words is not drawn (`replyOf`).
  if (outcome.kind === "assistant_reply") return replyOf(outcome) === undefined ? [] : [{ part: "reply", draft_id: draft }];
  const plan = outcomePlan(outcome);
  const refused = refusals(plan.refusals);
  const asked = plan.question;
  if (asked !== undefined) {
    // A question without its words is drawn as "can't do that yet", not as a question (`Chat.#ask`).
    if (asked.question_text === undefined) return refused;
    // DCTA-189: every question the card draws, in its order (two asked together are read one after the other).
    return [...refused, ...plan.questions.map((q): SpeechRef => ({ part: "question", draft_id: draft, ...(q.question_id === undefined ? {} : { question_id: q.question_id }) }))];
  }
  return [...ran, ...refused];
}
